import React, { useEffect, useMemo, useState } from 'react';
import { Message } from 'ai';
import cx from 'classnames';
import { motion } from 'framer-motion';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { parseAssistantMessage } from '@/lib/parse-assistant-message';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { getSearchUiContent } from '@/lib/ui-content/search-ui-content';

interface PreviewMessageProps {
  children?: React.ReactNode | null;
  isLoading: boolean;
  locale: LocaleDetails;
  message: Message;
  setInput: (input: string) => void;
  setSearchInput: (input: string) => void;
  selectedAnswer?: string;
  hidePrompt?: boolean;
  onSwitchToProductSearch?: () => void;
  currentSearchIntent?: any;
  /** Search finished with matched pages — the live results shelf takes over, render nothing. */
  isDoneWithMatches?: boolean;
  /** When true, hide the product-search suggestion chips (used while the PromptBanner typewriter runs on the question). */
  suggestionsHidden?: boolean;
}

// Render inline markdown formatting (bold/italic). Module-scope so the discussion
// thread in messages.tsx renders assistant bubbles with the exact same rules.
export const renderInlineMarkdown = (text: string) => {
  // Split by bold and italic markers
  const parts = text.split(/(\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith('***') && part.endsWith('***')) {
      return (
        <strong key={index} className="font-semibold italic">
          {part.slice(3, -3)}
        </strong>
      );
    } else if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    } else if (part.startsWith('*') && part.endsWith('*')) {
      return (
        <em key={index} className="italic">
          {part.slice(1, -1)}
        </em>
      );
    }
    return part;
  });
};

// Parse markdown with proper formatting (paragraph blocks + bullet lists)
export const parseMarkdownBlocks = (text: string | null) => {
  if (!text) return text;

  // Normalize line endings and spacing
  const normalizedText = text
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // Split text into paragraph blocks
  const blocks = normalizedText.split('\n\n').filter((block) => block.trim());

  return (
    <div className="space-y-4">
      {blocks.map((block, blockIndex) => {
        const lines = block.split('\n').filter((line) => line.trim());

        // Detect bullet point lists
        const hasBullets = lines.some((line) => /^\s*[-•*]\s+/.test(line));

        if (hasBullets) {
          // Render as bullet list
          const listItems: React.ReactNode[] = [];
          let currentHeader = '';

          lines.forEach((line, lineIndex) => {
            const trimmedLine = line.trim();

            // Match bullet point pattern
            const bulletMatch = trimmedLine.match(/^[-•*]\s+(.+)$/);

            if (bulletMatch) {
              const bulletContent = bulletMatch[1];
              listItems.push(
                <div key={`${blockIndex}-${lineIndex}`} className="flex gap-3 items-start">
                  <span className="text-blue-500 dark:text-blue-400 mt-1 flex-shrink-0 text-base">•</span>
                  <span className="text-base leading-relaxed">{renderInlineMarkdown(bulletContent)}</span>
                </div>,
              );
            } else if (trimmedLine && !trimmedLine.startsWith('**') && !currentHeader) {
              // Capture list header
              currentHeader = trimmedLine;
            }
          });

          return (
            <div key={blockIndex} className="space-y-3">
              {currentHeader && (
                <div className="font-semibold text-gray-900 dark:text-gray-100 text-base">
                  {renderInlineMarkdown(currentHeader)}
                </div>
              )}
              <div className="space-y-2.5 ml-1">{listItems}</div>
            </div>
          );
        } else {
          // Render as paragraph
          const content = lines.join(' ').trim();
          return (
            <div key={blockIndex} className="text-base leading-relaxed text-gray-700 dark:text-gray-300">
              {renderInlineMarkdown(content)}
            </div>
          );
        }
      })}
    </div>
  );
};

export const ButtonSuggestion = ({
  children,
  choice,
  handleSuggestionClick,
  onMouseDown,
  isLoading,
}: {
  children?: React.ReactNode;
  choice: string;
  handleSuggestionClick: (choice: string) => void;
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
  isLoading?: boolean;
}) => {
  return (
    <motion.button
      key={choice}
      disabled={isLoading}
      initial={{ opacity: 0, scale: 0.92, y: 4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 320, damping: 24 }}
      className="rounded-xl border border-slate-200/80 dark:border-slate-700/60 bg-white/60 dark:bg-[rgb(11,14,48)]/50 backdrop-blur-sm px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-cornflowerblue/10 dark:hover:bg-cornflowerblue/15 hover:border-cornflowerblue/50 dark:hover:border-cornflowerblue/40 hover:text-cornflowerblue dark:hover:text-cornflowerblue transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
      onClick={() => handleSuggestionClick(choice)}
      onMouseDown={onMouseDown}
    >
      {children ? (
        <span className="flex items-center gap-1.5">
          {children}
        </span>
      ) : (
        choice
      )}
    </motion.button>
  );
};

function AnswerVote({ likeLabel, dislikeLabel }: { likeLabel: string; dislikeLabel: string }) {
  const [vote, setVote] = useState<'up' | 'down' | null>(null);

  return (
    <div className="mt-2 flex justify-end gap-0.5" data-answer-vote={vote ?? 'none'}>
      <VoteButton
        label={likeLabel}
        pressed={vote === 'up'}
        onClick={() => setVote((current) => (current === 'up' ? null : 'up'))}
      >
        <ThumbsUp className="h-3.5 w-3.5" strokeWidth={1.75} fill={vote === 'up' ? 'currentColor' : 'none'} fillOpacity={vote === 'up' ? 0.2 : 0} />
      </VoteButton>
      <VoteButton
        label={dislikeLabel}
        pressed={vote === 'down'}
        onClick={() => setVote((current) => (current === 'down' ? null : 'down'))}
      >
        <ThumbsDown className="h-3.5 w-3.5" strokeWidth={1.75} fill={vote === 'down' ? 'currentColor' : 'none'} fillOpacity={vote === 'down' ? 0.2 : 0} />
      </VoteButton>
    </div>
  );
}

function VoteButton({
  children,
  label,
  pressed,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      onClick={onClick}
      className={cx(
        'grid h-7 w-7 place-items-center rounded-full transition-colors duration-200',
        pressed
          ? 'bg-cornflowerblue/10 text-cornflowerblue dark:bg-cornflowerblue/15'
          : 'text-gray-400 hover:bg-gray-100 hover:text-cornflowerblue dark:text-gray-500 dark:hover:bg-white/5 dark:hover:text-cornflowerblue',
      )}
    >
      {children}
    </button>
  );
}

export const PreviewMessage = ({
  children,
  isLoading,
  locale,
  message,
  selectedAnswer: propSelectedAnswer,
  setInput,
  setSearchInput,
  hidePrompt = false,
  onSwitchToProductSearch,
  currentSearchIntent,
  isDoneWithMatches = false,
  suggestionsHidden = false,
}: PreviewMessageProps) => {
  const [localSelectedAnswer, setLocalSelectedAnswer] = useState<string | null>(propSelectedAnswer || null);
  const searchContent = getSearchUiContent(locale);

  const { content, role } = message;
  // Extract message properties
  const { prompt, suggestedUserAnswers, isDone, productReferences, followUpSuggestions } = useMemo(() => {
    return parseAssistantMessage({ message });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, role]);
  const answerVisible = role !== 'user' && !hidePrompt && !!prompt?.trim();
  const [voteReady, setVoteReady] = useState(answerVisible && !isLoading);
  useEffect(() => {
    if (answerVisible && !isLoading) setVoteReady(true);
  }, [answerVisible, isLoading]);

  // Check if response contains product info
  const isPageQuestionResponse =
    (productReferences && productReferences.length > 0) || (followUpSuggestions && followUpSuggestions.length > 0);

  // Handle suggestion button clicks
  const handleSuggestionClick = (choice: string) => {
    if (choice === searchContent.skipInput) {
      setInput('');
      setTimeout(() => setInput(searchContent.skipInput), 0);
      return;
    }
    setLocalSelectedAnswer(choice);
    setInput(choice);
  };

  if (isDoneWithMatches && isDone) {
    return <></>;
  }
  const effectiveAnswer = localSelectedAnswer ?? propSelectedAnswer;
  const isSkipSelection =
    effectiveAnswer === searchContent.skipInput || effectiveAnswer === searchContent.skip;
  return (
    <>
      {!hidePrompt && (
        <motion.div
          className={cx('group/message mx-auto w-full max-w-3xl')}
          initial={{ y: 5, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          data-role={role}
        >
          <div
            className={cx(
              'group-data-[role:user]/message:ml-auto group-data-[role:user]/message:max-w-2xl group-data-[role:user]/message:w-fit flex w-full gap-4',
            )}
          >
            <div className="flex w-full flex-col">
              <div className="flex flex-row items-start gap-2">
                <div
                  className={`w-full ${
                    role === 'user'
                      ? 'text-primary-foreground rounded-xl bg-orange-800 px-3 py-2'
                      : 'text-gray-800 dark:text-gray-200 leading-relaxed'
                  }`}
                >
                  {parseMarkdownBlocks(prompt)}
                  {voteReady && (
                    <AnswerVote
                      likeLabel={searchContent.likeAnswer ?? 'Like this answer'}
                      dislikeLabel={searchContent.dislikeAnswer ?? 'Dislike this answer'}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Display suggestions based on response type */}
      <div className="flex flex-wrap justify-center gap-2.5 mx-auto">
        {effectiveAnswer && !isSkipSelection ? null : isPageQuestionResponse ? (
          // Follow-up suggestions for page questions
          <div className="w-full space-y-3">
            {followUpSuggestions && followUpSuggestions.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 font-medium">💡 {searchContent.followUpSuggestions}</p>
                <div className="flex flex-wrap gap-2.5">
                  {followUpSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => setInput(suggestion)}
                      className="rounded-lg bg-gray-100/80 dark:bg-dark/40 hover:bg-gray-200 dark:hover:bg-gray-700/50 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer transition-all duration-200 border border-gray-200 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Switch to product search button - Always show for page questions */}
            {onSwitchToProductSearch && currentSearchIntent === 'page-question' && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col items-center gap-2.5">
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    {searchContent.searchForProductsInstead}
                  </p>
                  <button
                    onClick={onSwitchToProductSearch}
                    className="group flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 dark:from-blue-600/90 dark:to-blue-700/90 dark:hover:from-blue-700 dark:hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>{searchContent.searchForProducts}</span>
                    <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : !isDone ? (
          // Suggested answers for product search — gated by PromptBanner typewriter completion
          suggestionsHidden ? null : (
          <>
            {suggestedUserAnswers.map((choice) => (
              <ButtonSuggestion
                isLoading={isLoading}
                choice={choice}
                handleSuggestionClick={handleSuggestionClick}
                key={choice}
              />
            ))}
            {!isLoading && suggestedUserAnswers.length > 0 && (() => {
              const capitalizedCount = suggestedUserAnswers.filter(
                (c) => c.length > 0 && c[0] === c[0].toUpperCase() && c[0] !== c[0].toLowerCase()
              ).length;
              const useUpperCase = capitalizedCount > suggestedUserAnswers.length / 2;
              const label = useUpperCase
                ? searchContent.skipChoice.charAt(0).toUpperCase() + searchContent.skipChoice.slice(1)
                : searchContent.skipChoice.charAt(0).toLowerCase() + searchContent.skipChoice.slice(1);
              return (
                <ButtonSuggestion
                  isLoading={isLoading}
                  choice={searchContent.skipInput}
                  handleSuggestionClick={handleSuggestionClick}
                >
                  {label}
                </ButtonSuggestion>
              );
            })()}
            {children}
          </>
          )
        ) : null}
      </div>

    </>
  );
};
